import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { authService } from '../services/authService';
import BrandLogo from './BrandLogo';

type Status = 'confirming' | 'success' | 'error' | 'signInRequired';

const ConfirmEmailChange: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState<Status>('confirming');
  const [error, setError] = useState<string | null>(null);
  const [confirmedEmail, setConfirmedEmail] = useState('');
  // The link may be opened twice (email clients prefetch); only spend the token once
  const hasRun = useRef(false);

  // Settings now lives as a tab on the profile page rather than its own route
  const profilePath = () => {
    const user = authService.getUser();
    return user ? `/u/${user.userId}` : '/';
  };

  const confirm = useCallback(async () => {
    if (!token) {
      setStatus('error');
      setError('This confirmation link is incomplete. Please request the email change again.');
      return;
    }

    if (!authService.isAuthenticated()) {
      // The link lands from an email, so the user may be signed out or on another device
      setStatus('signInRequired');
      return;
    }

    setStatus('confirming');
    setError(null);

    const result = await authService.confirmEmailChange(token);
    if (result.success) {
      setConfirmedEmail(result.user?.email ?? '');
      setStatus('success');
    } else {
      setError(result.error || 'We could not confirm your new email address.');
      setStatus('error');
    }
  }, [token]);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    confirm();
  }, [confirm]);

  return (
    <Box display="flex" justifyContent="center" alignItems="flex-start" sx={{ padding: 2 }}>
      <Paper
        elevation={6}
        sx={{
          padding: { xs: 3, sm: 4 },
          maxWidth: 450,
          width: '100%',
          borderRadius: 4,
          border: '1px solid rgba(17, 191, 174, 0.18)',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(244,252,255,0.94) 100%)',
          boxShadow: '0 24px 60px rgba(5, 27, 42, 0.22)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <BrandLogo size={70} stacked textColor="#08263A" accentColor="#0EAFA0" />
        </Box>

        <Typography variant="h6" align="center" sx={{ fontWeight: 700, mb: 2 }}>
          Confirm your email address
        </Typography>

        {status === 'confirming' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="textSecondary">
              Confirming your new email address…
            </Typography>
          </Box>
        )}

        {status === 'signInRequired' && (
          <>
            <Alert severity="info">
              Sign in with your <strong>current</strong> email address to finish confirming the change,
              then open this link again.
            </Alert>
            <Button fullWidth variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/')}>
              Go to Sign In
            </Button>
          </>
        )}

        {status === 'success' && (
          <>
            <Alert severity="success">
              Your email address has been updated{confirmedEmail ? ` to ${confirmedEmail}` : ''}. Use it to sign in from now on.
            </Alert>
            <Button fullWidth variant="contained" sx={{ mt: 2 }} onClick={() => navigate(profilePath())}>
              Back to Settings
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Alert severity="error">{error}</Alert>
            <Button fullWidth variant="contained" sx={{ mt: 2 }} onClick={() => navigate(profilePath())}>
              Back to Settings
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ConfirmEmailChange;
